import type { FloorTable, Room } from '@/features/floor-plan'
import { MAX_TABLES_PER_ROOM } from '../config'
import type { RoomPlan } from '../schema/elements'
import { SEAT_REACH } from '../render/seatLayout'

/**
 * Turning the floor planner's live plan into the copy a page carries.
 *
 * The planner keeps rooms and tables in two flat lists and draws them on a
 * canvas; a published page has no canvas and no session to fetch them with, so
 * one room is copied into the document and drawn from there. Everything below
 * is about making that copy draw the same picture the planner shows.
 */

/** One table as the planner stores it, with only what a drawing needs. */
export function roomPlanFrom(room: Room, tables: FloorTable[]): RoomPlan {
  return {
    id: room.id,
    name: room.name,
    tables: tables
      .filter(table => table.roomId === room.id)
      .slice(0, MAX_TABLES_PER_ROOM)
      .map(table => ({
        id: table.id,
        label: table.label,
        shape: table.shape,
        seatCount: table.seatCount,
        x: table.x,
        y: table.y,
        width: table.width,
        height: table.height,
        rotation: table.rotation
      }))
  }
}

export interface ViewBox {
  x: number
  y: number
  width: number
  height: number
}

const RADIANS = Math.PI / 180

/**
 * How much room one table takes once it has been turned.
 *
 * The planner rotates a table about its CENTRE: its group sits at the middle of
 * the bounding box and the shape is drawn back from there by half its size. A
 * turned rectangle therefore reaches further than its width and height say, and
 * a drawing sized from those alone clips the corners off every angled table.
 *
 * A circle is the exception and is not widened at all. The same formula applied
 * to it would grow it by up to 41% at 45 degrees, for a shape that turning does
 * not move at all.
 */
function halfExtent(table: RoomPlan['tables'][number], seats: number) {
  const halfWidth = table.width / 2 + seats
  const halfHeight = table.height / 2 + seats

  if (table.shape === 'round') return { x: halfWidth, y: halfHeight }

  const cos = Math.abs(Math.cos(table.rotation * RADIANS))
  const sin = Math.abs(Math.sin(table.rotation * RADIANS))

  return {
    x: halfWidth * cos + halfHeight * sin,
    y: halfWidth * sin + halfHeight * cos
  }
}

/** The centre a table is drawn and turned about. */
export function tableCentre(table: RoomPlan['tables'][number]) {
  return { x: table.x + table.width / 2, y: table.y + table.height / 2 }
}

/**
 * The box the whole room fits in.
 *
 * Sized from the tables rather than from the planner's canvas, because the
 * canvas is as big as the browser window it was drawn in and a room of four
 * tables would arrive as four specks in a field of nothing.
 *
 * No margin of its own. One was added here at first and it was wrong: being
 * inside the viewBox it grew with the plan, so zooming in bought more
 * emptiness along with the tables. The clear floor around the plan is the
 * frame's padding in `site.scss`, a pixel measure that stays the same at every
 * zoom, which is also how the waiter app pads its canvas.
 */
export function roomPlanViewBox(
  room: RoomPlan,
  { padding = 0, seats = false }: { padding?: number; seats?: boolean } = {}
): ViewBox {
  if (room.tables.length === 0) {
    return { x: 0, y: 0, width: 1, height: 1 }
  }

  // A ring of chairs stands outside the table it belongs to, so the box has to
  // hold them too or the outermost chair in the room is cut in half.
  const reach = seats ? SEAT_REACH : 0

  let left = Infinity
  let top = Infinity
  let right = -Infinity
  let bottom = -Infinity

  room.tables.forEach(table => {
    const centre = tableCentre(table)
    const half = halfExtent(table, reach)
    left = Math.min(left, centre.x - half.x)
    top = Math.min(top, centre.y - half.y)
    right = Math.max(right, centre.x + half.x)
    bottom = Math.max(bottom, centre.y + half.y)
  })

  return {
    x: left - padding,
    y: top - padding,
    width: right - left + padding * 2,
    height: bottom - top + padding * 2
  }
}

/**
 * Whether there is room to draw the chairs.
 *
 * Straight from the waiter app, which learned it the hard way: a ring reaches a
 * fixed distance past its table, so two tables closer together than a ring on
 * each side render their chairs on top of one another. When the tightest pair
 * in the room is that close, every table is drawn bare rather than some rooms
 * coming out as a tangle.
 *
 * One decision for the whole room, not per table: chairs around half the tables
 * and not the others reads as a mistake rather than as a rule.
 */
export function shouldDrawSeats(room: RoomPlan): boolean {
  if (room.tables.every(table => table.seatCount <= 0)) return false
  if (room.tables.length < 2) return true

  const circles = room.tables.map(table => {
    const centre = tableCentre(table)
    return {
      ...centre,
      radius: Math.max(table.width, table.height) / 2
    }
  })

  let smallest = Infinity
  for (let i = 0; i < circles.length; i += 1) {
    for (let j = i + 1; j < circles.length; j += 1) {
      const gap =
        Math.hypot(circles[i].x - circles[j].x, circles[i].y - circles[j].y) -
        circles[i].radius -
        circles[j].radius
      if (gap < smallest) smallest = gap
    }
  }

  return smallest >= SEAT_REACH * 2
}

/*
  Never shrink a table below this, in the planner's own units.

  Straight from the waiter app, which fits the room to the screen unless doing
  so would take a table under 44px, and then scales UP instead and lets the plan
  be panned. The reason is the same here and worse: a phone is 360px wide, and a
  twelve-table room squeezed into it draws tables a few pixels across with a
  number nobody can read. A plan that has to be scrolled is worth more than a
  plan that fits and says nothing.
*/
const MIN_TABLE_SPAN = 44

/**
 * The smallest scale the room may be drawn at, as a share of its own units.
 *
 * The waiter app takes `max(fitScale, minScale)`: fit the room to the box, and
 * if fitting would take a table under the readable size, use this instead and
 * let the plan be panned. This is that `minScale`, and it is deliberately NOT
 * floored at 1: a room of big tables may shrink a long way before anything is
 * lost, and holding every plan at its full size would mean no room ever fitted
 * a phone.
 */
export function roomPlanMinScale(room: RoomPlan): number {
  const smallest = room.tables.reduce(
    (span, table) => Math.min(span, table.width, table.height),
    Infinity
  )
  if (!Number.isFinite(smallest) || smallest <= 0) return 1
  return MIN_TABLE_SPAN / smallest
}

export interface RoomPlanSize {
  /** The drawing at the size it was drawn, in pixels. */
  width: number
  height: number
  /** The narrowest the drawing may go before its smallest table stops reading. */
  minWidth: number
}

/**
 * The three lengths the page sizes a room by: how big the drawing is, and how
 * small it may be made. The frame takes its shape from the first two and the
 * drawing its floor from the third; both read them from the same place so the
 * frame is never a different shape from the plan inside it.
 *
 * Not rounded. The frame keeps the drawing's proportions with `aspect-ratio`,
 * and a width and height rounded separately are a slightly different ratio
 * from the viewBox, which is a plan a pixel taller than its frame and a glow
 * lit for a pixel of travel nobody can make.
 */
export function roomPlanSize(room: RoomPlan): RoomPlanSize {
  const box = roomPlanViewBox(room, { seats: shouldDrawSeats(room) })
  return {
    width: box.width,
    height: box.height,
    minWidth: box.width * roomPlanMinScale(room)
  }
}

export interface TablePickBox {
  id: string
  label: string
  shape: RoomPlan['tables'][number]['shape']
  /** Left edge and top edge of the unturned table, as a percentage of the drawing. */
  left: number
  top: number
  width: number
  height: number
  rotation: number
}

/**
 * Where each table stands, as percentages of the drawing it stands in.
 *
 * The picker over the plan is HTML, not SVG: a form control cannot live inside
 * an `<svg>`, so every table gets a label laid over the drawing and the label
 * has to follow the table when the drawing scales. Percentages of the same box
 * the viewBox describes are the one unit that does that without a script.
 *
 * The box is the table alone, not its ring of chairs. A guest picks the table
 * they will sit at; the chairs stand on the floor between tables, and a hit
 * area that included them would overlap the next table's.
 */
export function tablePickBoxes(room: RoomPlan): TablePickBox[] {
  const box = roomPlanViewBox(room, { seats: shouldDrawSeats(room) })
  return room.tables.map(table => {
    const centre = tableCentre(table)
    // A round table is drawn as a circle of its width, whatever its height says.
    const height = table.shape === 'round' ? table.width : table.height
    return {
      id: table.id,
      label: table.label,
      shape: table.shape,
      left: ((centre.x - table.width / 2 - box.x) / box.width) * 100,
      top: ((centre.y - height / 2 - box.y) / box.height) * 100,
      width: (table.width / box.width) * 100,
      height: (height / box.height) * 100,
      rotation: table.rotation
    }
  })
}
